import { useEffect, useRef, useState } from 'react';
import React from 'react';
import ReactPortal from '../../portals/portals';
import { NexusModal, NexusModalFooter, NexusModalBody, NexusModalHeader, NexusModalHeaderSubtitle } from '@nexus/react';
import ButtonComponent from '../button/button';

interface InputProps {
    fullscreen?: boolean;
    show: boolean;
    size?: 'full' | 'lg' | 'md' | 'xl' | 'xs';
    header: string | React.ReactNode;
    children?: React.ReactNode;
    closeModal: () => void;
    isButton?: boolean;
    headerClassName?: string;
    className?: string;
    btnLabel?: string;
    btnType?: 'primary' | 'primary-alt' | 'secondary' | 'ghost' | 'text' | 'text-alt' | 'warn';
    isBtnDisabled?: boolean;
    onclick?: () => void;
    secondaryBtnLabel?: string;
    onSecondaryBtnClick?: () => void;
    isApplyShow?: boolean;
    bodyClassName?: string;
    footerClassName?: string;
}

const ModalComponent: React.FC<InputProps> = ({
    children,
    fullscreen,
    show,
    size,
    header,
    closeModal,
    isButton,
    headerClassName,
    className,
    btnLabel,
    btnType,
    isBtnDisabled,
    onclick,
    secondaryBtnLabel,
    onSecondaryBtnClick,
    isApplyShow,
    bodyClassName,
    footerClassName,
}) => {
    const [isshow, setShow] = useState<boolean>(show);
    const modalRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        setShow(show);
    }, [show]);

    useEffect(() => {
        if (!isshow) {
            return;
        }

        requestAnimationFrame(() => {
            const modalElement = modalRef.current;
            if (!modalElement) {
                return;
            }

            const firstFocusable = modalElement.querySelector(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ) as HTMLElement | null;

            if (firstFocusable) {
                firstFocusable.focus();
                return;
            }

            modalElement.setAttribute('tabindex', '-1');
            modalElement.focus();
        });
    }, [isshow]);

    const onCloseModal = () => {
        setShow(false);
        closeModal();
    };

    return (
        <ReactPortal wrapperId='portal-root'>
            <NexusModal
                ref={modalRef as any}
                className={className}
                data-testid='overlay'
                id='modal'
                show={isshow}
                fullscreen={fullscreen}
                size={size}
            >
                <NexusModalHeader className={headerClassName} data-testid='modal-header' onCloseEvent={onCloseModal}>
                    {header}
                    <NexusModalHeaderSubtitle data-testid='modal-Placeholder'></NexusModalHeaderSubtitle>
                </NexusModalHeader>
                <NexusModalBody className={bodyClassName}>{children}</NexusModalBody>
                {isButton && btnLabel && btnType ? (
                    <NexusModalFooter
                        className={footerClassName}
                        data-testid='modal-btn'
                        style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}
                    >
                        {secondaryBtnLabel && (
                            <ButtonComponent
                                label={secondaryBtnLabel}
                                type={'secondary'}
                                onClick={onSecondaryBtnClick}
                            ></ButtonComponent>
                        )}
                        <ButtonComponent label={btnLabel} onClick={onclick} type={btnType} disabled={isBtnDisabled} />
                    </NexusModalFooter>
                ) : isApplyShow ? (
                    <button onClick={onCloseModal} data-testid='btn-confirm' className='nexus-btn-primary'>
                        Apply
                    </button>
                ) : null}
            </NexusModal>
        </ReactPortal>
    );
};

export default ModalComponent;
